import React, { Fragment, useEffect, useState } from "react";
import { Dialog as HeadlessDialog, Transition } from "@headlessui/react";
import Button from "src/base-components/Button";
import Lucide from "src/base-components/Lucide";
import { FormInput, FormSelect } from "src/base-components/Form";
import {
  completeReminderService,
  fetchReminderCalendarService,
  snoozeReminderService,
} from "src/services/reminder.services";

const GlobalReminderModals: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [completionRemarks, setCompletionRemarks] = useState("");
  const [snoozeTime, setSnoozeTime] = useState("15 Minutes");

  useEffect(() => {
    function handlerComplete(e: Event) {
      try {
        const ce = e as CustomEvent;
        const payload = ce.detail;
        if (payload && payload.item) {
          const item = payload.item;
          const remEvent: any = {
            id: item.reminder?.id || item.id,
            title: item.reminder?.title || item.title,
            description: item.reminder?.description || item.description,
            assignments: item.reminder?.assignments || item.assignments || [],
            reminder: item.reminder || null,
            query:
              item.reminder?.id || item.query || item.reminder_id || undefined,
          };
          setSelectedEvent(remEvent);
          setTimeout(() => setShowCompleteModal(true), 100);
        }
      } catch (err) {
        console.error("open-complete-modal handler error", err);
      }
    }

    function handlerSnooze(e: Event) {
      try {
        const ce = e as CustomEvent;
        const payload = ce.detail;
        if (payload && payload.item) {
          const item = payload.item;
          const remEvent: any = {
            id: item.reminder?.id || item.id,
            title: item.reminder?.title || item.title,
            description: item.reminder?.description || item.description,
            assignments: item.reminder?.assignments || item.assignments || [],
            reminder: item.reminder || null,
            query:
              item.reminder?.id || item.query || item.reminder_id || undefined,
          };
          setSelectedEvent(remEvent);
          setTimeout(() => setShowSnoozeModal(true), 100);
        }
      } catch (err) {
        console.error("open-snooze-modal handler error", err);
      }
    }

    window.addEventListener(
      "open-complete-modal",
      handlerComplete as EventListener
    );
    window.addEventListener(
      "open-snooze-modal",
      handlerSnooze as EventListener
    );
    return () => {
      window.removeEventListener(
        "open-complete-modal",
        handlerComplete as EventListener
      );
      window.removeEventListener(
        "open-snooze-modal",
        handlerSnooze as EventListener
      );
    };
  }, []);

  const convertSnoozeTimeToMinutes = (snoozeText: string) => {
    switch (snoozeText) {
      case "15 Minutes":
        return 15;
      case "30 Minutes":
        return 30;
      case "1 Hour":
        return 60;
      case "2 Hours":
        return 120;
      case "4 Hours":
        return 240;
      case "1 Day":
        return 1440;
      case "2 Days":
        return 2880;
      case "1 Week":
        return 10080;
      default:
        return 30;
    }
  };

  const handleCompleteReminder = async () => {
    if (!selectedEvent) return;
    try {
      const reminderId = selectedEvent.id;
      await completeReminderService({
        id: reminderId,
        completion_remarks: completionRemarks,
        callback: (hasError: boolean, data: any) => {
          if (!hasError) {
            // Notify listeners that a reminder changed so pages can refresh if needed
            try {
              window.dispatchEvent(
                new CustomEvent("reminder-updated", {
                  detail: { id: reminderId },
                })
              );
            } catch (e) {}
            setCompletionRemarks("");
            setShowCompleteModal(false);
            setSelectedEvent(null);
          } else {
            console.error("Error completing reminder:", data);
          }
        },
      });
    } catch (error) {
      console.error("Error completing reminder:", error);
    }
  };

  const handleSnoozeReminder = async () => {
    if (!selectedEvent) return;
    try {
      const reminderId = selectedEvent.id;
      const snoozeMinutes = convertSnoozeTimeToMinutes(snoozeTime);
      await snoozeReminderService({
        id: reminderId,
        snooze_minutes: snoozeMinutes,
        callback: (hasError: boolean, data: any) => {
          if (!hasError) {
            try {
              window.dispatchEvent(
                new CustomEvent("reminder-updated", {
                  detail: { id: reminderId },
                })
              );
            } catch (e) {}
            setSnoozeTime("15 Minutes");
            setShowSnoozeModal(false);
            setSelectedEvent(null);
          } else {
            console.error("Error snoozing reminder:", data);
          }
        },
      });
    } catch (error) {
      console.error("Error snoozing reminder:", error);
    }
  };

  return (
    <>
      {/* Snooze Modal */}
      <Transition appear show={showSnoozeModal} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowSnoozeModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessDialog.Panel
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-4">
                    <HeadlessDialog.Title className="text-lg font-semibold text-slate-800">
                      Snooze Reminder
                    </HeadlessDialog.Title>
                    <button
                      onClick={() => setShowSnoozeModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <Lucide icon="X" className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <p className="text-sm text-slate-600 mb-6">
                    Choose how long to snooze this reminder
                  </p>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Snooze Duration
                    </label>
                    <FormSelect
                      value={snoozeTime}
                      onChange={(e) => setSnoozeTime(e.target.value)}
                      className="w-full"
                    >
                      <option value="15 Minutes">15 Minutes</option>
                      <option value="30 Minutes">30 Minutes</option>
                      <option value="1 Hour">1 Hour</option>
                      <option value="2 Hours">2 Hours</option>
                      <option value="4 Hours">4 Hours</option>
                      <option value="1 Day">1 Day</option>
                      <option value="2 Days">2 Days</option>
                      <option value="1 Week">1 Week</option>
                    </FormSelect>
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      variant="outline-secondary"
                      className="flex-1"
                      onClick={() => setShowSnoozeModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="flex-1"
                      onClick={handleSnoozeReminder}
                    >
                      Snooze
                    </Button>
                  </div>
                </HeadlessDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessDialog>
      </Transition>

      {/* Complete Modal */}
      <Transition appear show={showCompleteModal} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowCompleteModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessDialog.Panel
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-4">
                    <HeadlessDialog.Title className="text-lg font-semibold text-slate-800">
                      Mark as Complete
                    </HeadlessDialog.Title>
                    <button
                      onClick={() => setShowCompleteModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <Lucide icon="X" className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <p className="text-sm text-slate-600 mb-6">
                    Add any remarks or notes about the completion of this
                    reminder (optional).
                  </p>

                  <div className="mb-6">
                    <FormInput
                      type="text"
                      placeholder="Enter completion remarks..."
                      value={completionRemarks}
                      onChange={(e) => setCompletionRemarks(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      variant="outline-secondary"
                      className="flex-1"
                      onClick={() => {
                        setCompletionRemarks("");
                        setShowCompleteModal(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="flex-1"
                      onClick={handleCompleteReminder}
                    >
                      Mark Complete
                    </Button>
                  </div>
                </HeadlessDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessDialog>
      </Transition>
    </>
  );
};

export default GlobalReminderModals;
